import React from "react"
import { Text } from "@chakra-ui/react"
import { useHistory } from "react-router-dom"
import { eventSettingsPath } from "utilities/routes"
import { CheckInLocation, ModalName } from "sharedTypes"
import Icon from "components/elements/Icon"
import { Combobox } from "components/Combobox"
import useModal from "services/useModal"
import SideToggle from "../SideToggle/SideToggle"

type Props = {
  checkInLocationsWithCheckInOption: (CheckInLocation | {id: null; name: string})[]
  checkInLocationId: number|null
  handleClickLocation: (locationId: number|null) => void
  eventId: string
  checkInLocationsEnabled: boolean
}

type ActivityItem = { id: number | null; name: string }

const SelectActivityButton = ({
  checkInLocationsWithCheckInOption,
  checkInLocationId,
  handleClickLocation,
  eventId,
  checkInLocationsEnabled,
}: Props) => {
  const showModal = useModal()
  const history = useHistory()

  const items = checkInLocationsWithCheckInOption as ActivityItem[]
  const current = items.find((i) => i.id === checkInLocationId) || null

  const onChange = (item: ActivityItem | null): boolean | void => {
    if (!item) return undefined
    // Free-plan gating: only the default "Check-In" option is selectable.
    if (!checkInLocationsEnabled && item.id !== null) return false
    handleClickLocation(item.id)

    return undefined
  }

  const onCreate = (name: string) => {
    showModal(ModalName.EventActivityForm, {
      onSave: ({ id }: { id: number }) => handleClickLocation(id),
      defaultName: name,
    })
  }

  return (
    <Combobox.Root<ActivityItem>
      items={items}
      getItemKey={(i) => (i.id === null ? "checkin" : String(i.id))}
      getItemLabel={(i) => i.name}
      value={current}
      onChange={onChange}
      onCreate={checkInLocationsEnabled ? onCreate : undefined}
    >
      <Combobox.Trigger>
        <SideToggle icon="session" title="Select Event Activity" />
      </Combobox.Trigger>
      <Combobox.Content w="320px" arrowBg="white">
        <Combobox.Input placeholder="Search Activities" />
        <Combobox.List<ActivityItem> maxH="40vh">
          {(item) => {
            const isCheckIn = item.id === null

            return (
              <Combobox.Item
                item={item}
                disabled={!checkInLocationsEnabled && !isCheckIn}
                // Per PM: Check-In reads as a "default action" distinct from
                // the user-created activities. Taller row + divider below it
                // gives it visual weight without splitting the listbox into
                // separate widgets.
                py={isCheckIn ? 5 : 3}
                divideAfter={isCheckIn}
              >
                {item.name}
              </Combobox.Item>
            )
          }}
        </Combobox.List>
        <Combobox.Create>
          {(query) => (
            <>
              <Icon icon="plus-big" size={5} mr={2} color="blue.400" />
              <Text>{query ? `New Event Activity "${query}"` : "New Event Activity"}</Text>
            </>
          )}
        </Combobox.Create>
        <Combobox.Action
          onSelect={() => history.push(eventSettingsPath(eventId, "event-activities"))}
          color="blue.400"
          // No top border when Create is gated out — its 1px would stack against
          // the Input's bottom border, producing a visible double rule.
          borderTopWidth={checkInLocationsEnabled ? 1 : 0}
          borderColor="gray.200"
        >
          <Icon icon="pencil" size={5} mr={2} color="blue.400" />
          <Text>Edit Event Activities</Text>
        </Combobox.Action>
      </Combobox.Content>
    </Combobox.Root>
  )
}

export default SelectActivityButton
